import { isDate, parse, subYears, fromUnixTime } from 'date-fns';
import * as Yup from 'yup';

function parseMMYYYYDateString(value, originalValue) {
  if (isDate(originalValue)) return originalValue;

  if (typeof originalValue === 'number' && !isNaN(originalValue)) {
    return fromUnixTime(originalValue / 1000);
  }

  if (typeof originalValue === 'string' && originalValue.includes('/')) {
    return parse(originalValue, 'MM/yyyy', new Date());
  }

  return parse(originalValue, 'yyyy-MM-dd', new Date());
}

export const WorkExperienceSchema = Yup.object().shape({
  workExperienceYears: Yup.number()
    .required('Work Experience Years is required.')
    .min(0, 'Work Experience Year cannot be less than 0')
    .max(80, 'Work Experience years cannot be greater than 80')
    .test('is-whole', 'This must be a whole number', (value) => Number.isInteger(value)),
  workExperienceMonths: Yup.number().optional().nullable(),
  careerPromotions: Yup.array()
    .of(
      Yup.object().shape({
        previousDesignation: Yup.string()
          .min(2, 'Too Short!')
          .max(25, 'Too Long!')
          .required('Previous Designation is required'),
        promotionYear: Yup.number()
          .min(new Date().getFullYear() - 80, 'Year should not be before 80 years from now')
          .max(new Date().getFullYear(), 'Year should not be greater than current year')
          .required('Promotion Year is required.'),
      })
    )
    .nullable(),
  languageProficiency: Yup.array().of(
    Yup.object().shape({
      language: Yup.string().required('Required'),
      proficiency: Yup.string().required('Required'),
    })
  ),
  previousJobs: Yup.array()
    .of(
      Yup.object().shape({
        top3Achievements: Yup.string().optional().nullable(),
        top3Responsibilities: Yup.string().optional().nullable(),
        // roleStartDate: Yup.date()
        //   .transform(parseMMYYYYDateString)
        //   .max(new Date(), 'Start Date should not be in the future.')
        //   .min(subYears(new Date(), 80), 'Start Date should not be more than 80 years ago.')
        //   .required('Start Date is required.'),

        roleStartDate: Yup.date()
          .transform((value, originalValue) => {
            if (!originalValue) return null; // allow empty
            if (Object.prototype.toString.call(originalValue) === '[object Date]') {
              return isNaN(originalValue.getTime()) ? null : originalValue;
            }
            const parsed = new Date(originalValue);
            return isNaN(parsed.getTime()) ? null : parsed;
          })
          .nullable()
          .max(new Date(), 'Start Date should not be in the future.')
          .min(subYears(new Date(), 80), 'Start Date should not be more than 80 years ago.'),

        roleEndDate: Yup.date()
          .transform((value, originalValue) => {
            // If empty input (null, undefined, ""), treat as null
            if (!originalValue) return null;

            // If a Date object, return as is
            if (Object.prototype.toString.call(originalValue) === '[object Date]') {
              return isNaN(originalValue.getTime()) ? null : originalValue;
            }

            // Try parse string input to date
            const parsed = new Date(originalValue);
            return isNaN(parsed.getTime()) ? null : parsed;
          })
          .nullable()
          .max(new Date(), 'End Date should not be in the future.')
          .min(subYears(new Date(), 80), 'End Date should not be more than 80 years ago.'),

        industrySector: Yup.string().required('Industry Sector is required.'),
        organizationName: Yup.string().required('Organization Name is required.'),
        designation: Yup.string().required('Designation is required.'),
        reportingTo: Yup.string().optional().nullable(),
        numberOfReportees: Yup.number()
          .optional().nullable(),
        teamSize: Yup.number().optional().nullable(),
        careerPromotions: Yup.array().of(
          Yup.object().shape({
            previousDesignation: Yup.string().required('Designation is required.'),
            promotionYear: Yup.number()
              .min(new Date().getFullYear() - 80, 'Year should not be before 80 years from now')
              .max(new Date().getFullYear(), 'Year should not be greater than current year')
              .required('Promotion Year is required.'),
          })
        ),
      })
    )
    .nullable(),
  awards: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().nullable(),
      providedBy: Yup.string().nullable(),
      issueYear: Yup.number()
        .nullable()
        .min(new Date().getFullYear() - 80, 'Award year cannot be greater than 80 years.')
        .max(new Date().getFullYear(), 'Award year cannot be in the future.'),
    })
  ),

  careerGaps: Yup.array().of(
    Yup.object().shape({
      startDate: Yup.date()
        .transform(parseMMYYYYDateString)
        .max(new Date(), 'Date should not be a future date.')
        .min(subYears(new Date(), 80), 'Date should not be less than 80 years from today')
        .required('Start Date is required.'),
      endDate: Yup.date()
        .transform(parseMMYYYYDateString)
        .max(new Date(), 'Date should not be a future date.')
        .min(subYears(new Date(), 80), 'Date should not be less than 80 years from today')
        .nullable(),
      // reasons: Yup.string().required('Reason(s) is required.'),
    })
  ),
  technicalProficiencies: Yup.array()
    .of(
      Yup.object().shape({
        skill: Yup.string().required('Skill value is required'),
        rating: Yup.string().required('Rating is required'),
        keyProject: Yup.string().optional().nullable(),
      })
    )
    .nullable(),
  professionalMemberships: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Name is required'),
        year: Yup.number()
          .nullable()   // allow empty
          .transform((value, originalValue) => {
            // "" → null
            if (originalValue === '' || originalValue == null) return null;
            return Number(originalValue);
          })
          .min(new Date().getFullYear() - 80, 'Year should not be before 80 years.')
          .max(new Date().getFullYear(), 'Year cannot be in the future.')
      })
    )
    .nullable(),
  currentDesignation: Yup.string().required('Designation is required.'),
  currentOrganizationName: Yup.string().required('Organization Name is required.'),
  currentIndustrySector: Yup.string().required('Industry Sector is required.'),
  // currentNoticePeriod: Yup.number()
  // .nullable()
  //   .typeError('Please enter a valid number.')
  //   .test('is-whole', 'This must be a whole number', (value) => Number.isInteger(value)),
  // currentRoleStartDate: Yup.date()

  currentNoticePeriod: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      // Treat empty input as null
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null;
      }

      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .min(0, 'Notice period must be greater than or equal to 0.')
    .test('is-whole', 'This must be a whole number', (value) => {
      // Allow null / blank safely
      if (value === null || value === undefined) return true;
      return Number.isInteger(value);
    }),

  //   .transform(parseMMYYYYDateString)
  //   .max(new Date(), 'Date should not be a future date.')
  //   .min(subYears(new Date(), 80), 'Date should not be less than 80 years from today')
  //   .required('Start Date is required.'),

  currentRoleStartDate: Yup.date()
    .transform((value, originalValue) => {
      if (!originalValue) return null;

      if (Object.prototype.toString.call(originalValue) === '[object Date]') {
        return isNaN(originalValue.getTime()) ? null : originalValue;
      }

      const parsed = new Date(originalValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    })
    .nullable()
    .max(new Date(), 'Date should not be a future date.')
    .min(subYears(new Date(), 80), 'Date should not be more than 80 years ago.'),

  currenttop3Responsibilities: Yup.string().optional().nullable(),
  currenttop3Achievements: Yup.string().optional().nullable(),
  currentReportingTo: Yup.string().optional().nullable(),
  currentNumberOfReportees: Yup.number().optional().nullable(),
  currentTeamSize: Yup.number().optional().nullable(),
});